import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FaCopy, FaEdit, FaTrash, FaPaperPlane, FaCheck, FaTimes,
  FaFileSignature, FaFileInvoiceDollar, FaExternalLinkAlt
} from 'react-icons/fa';
import Modal from '../../components/shared/Modal';
import { ActionButton, StatusBadge } from '../../components/styled/CommonStyles';
import { formatCurrency } from '../../utils/utils';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { getUnitInfo } from '../estimates/ServiceModal';
import {
  fetchChangeOrderById,
  sendChangeOrder,
  approveChangeOrder,
  declineChangeOrder,
  deleteChangeOrder,
  getChangeOrderPublicUrl,
} from './changeOrderService';
import { discountInfo } from '../../utils/discount';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(2)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing(2)};
  flex-wrap: wrap;

  h4 {
    margin: 0;
    color: ${(props) => props.theme.colors.primary};
  }
`;

const Section = styled.div`
  padding: ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};

  .label {
    font-size: 12px;
    color: ${(props) => props.theme.colors.textSecondary};
    text-transform: uppercase;
    margin-bottom: 4px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing(1.5)} 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray}22;

  &:last-child { border-bottom: none; }
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${(props) => props.theme.spacing(2)};
  font-weight: bold;
  font-size: 1.1em;
`;

const PublicLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85em;
  word-break: break-all;
  padding: ${(props) => props.theme.spacing(1)};
  background: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.inputGray}44;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const STATUS_COLORS = {
  Draft: '#6c757d',
  Sent: '#007bff',
  Viewed: '#17a2b8',
  Approved: '#28a745',
  Declined: '#dc3545',
  Cancelled: '#6c757d',
};

const ChangeOrderDetailModal = ({
  isOpen,
  changeOrderId,
  sequenceLabel,
  onClose,
  onEdit,
  onChanged,
  onApproved,
}) => {
  const navigate = useNavigate();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [copiedAt, setCopiedAt] = useState(null);

  useEffect(() => {
    if (isOpen && changeOrderId) load();
    if (!isOpen) {
      setCo(null);
      setError('');
      setConfirmAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, changeOrderId]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchChangeOrderById(changeOrderId);
      setCo(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load change order');
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (fn, options = {}) => {
    try {
      setLoading(true);
      setError('');
      const result = await fn();
      if (options.afterApprove) {
        onApproved?.(result);
        onClose?.();
        return;
      }
      if (options.afterDelete) {
        onChanged?.();
        onClose?.();
        return;
      }
      onChanged?.();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Action failed');
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  const handleCopy = (url) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
    }
    setCopiedAt(Date.now());
    setTimeout(() => setCopiedAt(null), 1500);
  };

  if (!isOpen) return null;

  const status = co?.status;
  const publicUrl = co?.publicToken ? getChangeOrderPublicUrl(co.publicToken) : null;
  const canEdit = status === 'Draft';
  const canSend = status === 'Draft' || status === 'Sent' || status === 'Viewed';
  const isResend = status === 'Sent' || status === 'Viewed';
  const canApprove = status === 'Draft' || status === 'Sent' || status === 'Viewed';
  const approveNowAllowed = status === 'Draft' && co?.requiresSignature === false;
  const canDecline = status === 'Sent' || status === 'Viewed' || status === 'Draft';
  const canDelete = status !== 'Cancelled';
  const isApprovedDelete = status === 'Approved';
  const isCredit = co?.kind === 'Credit';

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={co ? `${sequenceLabel || 'Change Order'} — ${co.title}` : 'Change Order'}
        onCancel={onClose}
        hideFooter
        disableBackdropClose
      >
        {loading && !co && <LoadingSpinner />}
        {co && (
          <Wrapper>
            <HeaderRow>
              <h4>{sequenceLabel || 'Change Order'}</h4>
              <div style={{ display: 'flex', gap: 6 }}>
                {isCredit && <StatusBadge color="#1b7a36">Credit</StatusBadge>}
                <StatusBadge color={STATUS_COLORS[status] || '#6c757d'}>{status}</StatusBadge>
              </div>
            </HeaderRow>

            {co.description && (
              <Section>
                <div className="label">Description</div>
                <div>{co.description}</div>
              </Section>
            )}
            {co.reason && (
              <Section>
                <div className="label">Reason</div>
                <div>{co.reason}</div>
              </Section>
            )}

            <Section>
              <div className="label">Items</div>
              {(co.items || []).map((item, i) => {
                const unit = getUnitInfo(item.measurementUnit).short;
                return (
                  <ItemRow key={i}>
                    <div>
                      <strong>{item.Service?.service_name || item.serviceName}</strong>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {item.totalArea} {unit} · {formatCurrency(item.pricePerSqft)}/{unit}
                      </div>
                      {item.observation && (
                        <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>{item.observation}</div>
                      )}
                    </div>
                    <strong>{formatCurrency(item.totalPrice)}</strong>
                  </ItemRow>
                );
              })}
              {(() => {
                const discount = discountInfo(co, co.subtotal);
                if (isCredit || !discount.hasDiscount) return null;
                return (
                  <TotalsRow>
                    <span>{discount.label}{discount.reason ? ` · ${discount.reason}` : ''}</span>
                    <span>- {formatCurrency(discount.amount)}</span>
                  </TotalsRow>
                );
              })()}
              <TotalsRow>
                <span>{isCredit ? 'Total Credit' : 'Total'}</span>
                <span>{formatCurrency(co.totalPrice)}</span>
              </TotalsRow>
            </Section>

            {publicUrl && (status === 'Sent' || status === 'Viewed') && (
              <Section>
                <div className="label">Customer Link</div>
                <PublicLink>
                  <span style={{ flex: 1 }}>{publicUrl}</span>
                  <ActionButton type="button" onClick={() => handleCopy(publicUrl)}>
                    <FaCopy /> {copiedAt ? 'Copied!' : 'Copy'}
                  </ActionButton>
                  <ActionButton type="button" onClick={() => window.open(publicUrl, '_blank')}>
                    <FaExternalLinkAlt />
                  </ActionButton>
                </PublicLink>
              </Section>
            )}

            {co.signature && status === 'Approved' && (
              <Section>
                <div className="label">Signed</div>
                <div>
                  <FaFileSignature /> {co.signature.fullName} ({co.signature.email})
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {co.signature.signedAt && new Date(co.signature.signedAt).toLocaleString()}
                </div>
              </Section>
            )}

            {co.approvedAt && status === 'Approved' && !co.signature && (
              <Section>
                <div className="label">Approved At</div>
                <div>{new Date(co.approvedAt).toLocaleString()}</div>
              </Section>
            )}

            {co.declinedAt && status === 'Declined' && (
              <Section>
                <div className="label">Declined At</div>
                <div>{new Date(co.declinedAt).toLocaleString()}</div>
              </Section>
            )}

            {co.invoice && status === 'Approved' && (
              <Section>
                <div className="label">Change Order Invoice</div>
                <ActionButton
                  type="button"
                  variant="primary"
                  onClick={() => { onClose?.(); navigate(`/financial/invoices/${co.invoice.id}`); }}
                >
                  <FaFileInvoiceDollar /> {co.invoice.invoiceNumber} · {formatCurrency(co.invoice.totalAmount)}
                </ActionButton>
              </Section>
            )}

            {isCredit && status === 'Approved' && (
              <Section>
                <div className="label">Invoice</div>
                <div>Deducted from the original invoice for this quote.</div>
              </Section>
            )}

            {error && (
              <div style={{ color: '#dc3545', fontSize: '0.9em' }}>{error}</div>
            )}

            <Actions>
              {canEdit && (
                <ActionButton type="button" onClick={() => { onEdit?.(co); }}>
                  <FaEdit /> Edit
                </ActionButton>
              )}
              {canSend && (
                <ActionButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  onClick={() => setConfirmAction('send')}
                >
                  <FaPaperPlane /> {isResend ? 'Resend to Customer' : 'Send to Customer'}
                </ActionButton>
              )}
              {approveNowAllowed && (
                <ActionButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  onClick={() => setConfirmAction('approveNow')}
                >
                  <FaCheck /> Approve Now
                </ActionButton>
              )}
              {canApprove && !approveNowAllowed && status !== 'Draft' && (
                <ActionButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  onClick={() => setConfirmAction('approve')}
                >
                  <FaCheck /> Approve
                </ActionButton>
              )}
              {canDecline && (
                <ActionButton
                  type="button"
                  variant="danger"
                  disabled={loading}
                  onClick={() => setConfirmAction('decline')}
                >
                  <FaTimes /> Decline
                </ActionButton>
              )}
              {canDelete && (
                <ActionButton
                  type="button"
                  variant="danger"
                  disabled={loading}
                  onClick={() => setConfirmAction('delete')}
                >
                  <FaTrash /> Delete
                </ActionButton>
              )}
              <ActionButton type="button" onClick={onClose}>Close</ActionButton>
            </Actions>
          </Wrapper>
        )}
      </Modal>

      <Modal
        isOpen={confirmAction === 'send'}
        zIndex={1200}
        title={isResend ? 'Resend Change Order' : 'Send Change Order'}
        message={isResend
          ? 'Resend this change order to the customer? They will receive another email with the signing link.'
          : 'Send this change order to the customer? They will receive an email with a signing link.'}
        onConfirm={() => runAction(() => sendChangeOrder(changeOrderId))}
        onCancel={() => setConfirmAction(null)}
      />
      <Modal
        isOpen={confirmAction === 'approve' || confirmAction === 'approveNow'}
        zIndex={1200}
        title="Approve Change Order"
        message={isCredit
          ? 'Approving will lower the estimate and job totals and deduct this credit from the original invoice. Continue?'
          : 'Approving will update the estimate and job totals and generate a new change order invoice. Continue?'}
        onConfirm={() => runAction(() => approveChangeOrder(changeOrderId), { afterApprove: true })}
        onCancel={() => setConfirmAction(null)}
      />
      <Modal
        isOpen={confirmAction === 'decline'}
        zIndex={1200}
        title="Decline Change Order"
        message="Mark this change order as declined? This cannot be undone."
        onConfirm={() => runAction(() => declineChangeOrder(changeOrderId))}
        onCancel={() => setConfirmAction(null)}
      />
      <Modal
        isOpen={confirmAction === 'delete'}
        zIndex={1200}
        title="Delete Change Order"
        message={isApprovedDelete && isCredit
          ? 'This credit is APPROVED. Deleting it will restore the estimate/job total and add the credit back onto the original invoice. Continue?'
          : isApprovedDelete
          ? 'This change order is APPROVED. Deleting it will reverse the estimate/job total, remove its invoice, and delete the signature. This is not allowed if the CO invoice has recorded payments. Continue?'
          : 'Delete this change order permanently?'}
        onConfirm={() => runAction(() => deleteChangeOrder(changeOrderId), { afterDelete: true })}
        onCancel={() => setConfirmAction(null)}
      />
    </>
  );
};

export default ChangeOrderDetailModal;
